import type { GanOauth } from 'types';
import { Button } from '@/components/ui/button';
import { useGoogleAccounts } from '@/state/googleAnalytics/useGoogleAccounts';
import { DataTable, useDataControl } from '@/components/DataTable/DataTable';
import { RefreshCw, Plus, BarChart3 } from 'lucide-react';
import { Avatar } from '@/components/Avatar';
import { SearchInput } from '@/components/SearchInput';
import { SEARCH_FIELD_CLASS } from '../../ConsolePanel';
import { FirstRunState } from '../../FirstRunState';

export const GoogleAnalyticsScreenContent = () => {
  const googleAccountsManager = useGoogleAccounts();
  const { googleAccounts, onConnect, refresh } = googleAccountsManager;
  const dataController = useDataControl<GanOauth>(googleAccounts);
  const { searchQuery, setSearchQuery } = dataController;

  return (
    <div className="flex flex-col gap-[14px]">
      {googleAccounts && (
        <section className="bg-panel border-line rounded-panel border">
          {/* Stacked below `md`: "Connect a Google account" alone is most of a 375px
              line. `relative` on the search wrapper because SearchInput collapses to a
              magnifier and drops its field in absolutely. */}
          <div className="border-line flex flex-col gap-2 border-b px-4 py-3 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-3">
            <h2 className="text-foreground m-0 text-body font-medium">Connected accounts</h2>
            <div className="flex flex-col gap-2 md:shrink-0 md:flex-row md:items-center">
              <div className="relative w-full md:w-auto">
                <SearchInput
                  placeholder="Search accounts..."
                  value={searchQuery}
                  handleSearch={setSearchQuery}
                  className={SEARCH_FIELD_CLASS}
                />
              </div>
              <Button onClick={onConnect} className="h-9 w-full rounded-lg md:w-auto" data-tour="ga-connect">
                <Plus className="mr-2 h-4 w-4" />
                Connect a Google account
              </Button>
            </div>
          </div>

          <div className="p-3">
            {dataController.rawData?.length === 0 ? (
              <FirstRunState
                icon={BarChart3}
                title="No Google account connected"
                description="Connect the Google account that owns your Google Analytics property and Pertento can send each experiment's variants to it as audiences and read conversions back. It is optional: websites can also be measured by Pertento itself."
                action={
                  <Button onClick={onConnect} className="h-9 rounded-lg">
                    <Plus className="mr-2 h-4 w-4" />
                    Connect a Google account
                  </Button>
                }
              />
            ) : (
              <DataTable
                dataController={dataController}
                searchKeys={['name', 'email']}
                pageSize={10}
                noDataMessage="No Google accounts connected."
                noDataCTA={
                  <Button onClick={onConnect} className="mt-4 h-9 w-full rounded-lg min-[500px]:w-auto">
                    <Plus className="mr-2 h-4 w-4" />
                    Connect your first Google account
                  </Button>
                }
                columns={[
                  {
                    field: 'image',
                    label: 'Image',
                    headerClassName: 'micro-label w-16',
                    format: ({ value }) => <Avatar className="h-8 w-8 rounded-full" src={value as string} />,
                  },
                  {
                    field: 'name',
                    label: 'Name',
                    sortKey: 'name',
                    headerClassName: 'micro-label w-96',
                    cellClassName: 'text-foreground min-w-0 truncate',
                  },
                  {
                    field: 'email',
                    label: 'Email',
                    sortKey: 'email',
                    headerClassName: 'micro-label w-128',
                    format: ({ value, idx }) => (
                      <div
                        className="text-dim min-w-0 truncate max-[499px]:px-2"
                        data-tour={idx === 0 ? 'ga-login' : undefined}
                      >
                        {value}
                      </div>
                    ),
                  },
                  {
                    field: 'accountsCount',
                    label: '# Accounts',
                    sortKey: 'accountsCount',
                    labelMobile: '#',
                    headerClassName: 'micro-label w-32 text-center',
                    cellClassName: 'num text-center',
                    // Anchors go on the first row only, so each tour step finds one element.
                    format: ({ value, idx }) => (
                      <span data-tour={idx === 0 ? 'ga-accounts' : undefined}>{value as number | null}</span>
                    ),
                  },
                  {
                    field: 'lastRefreshed',
                    label: 'Last Updated',
                    sortKey: 'lastRefreshed',
                    headerClassName: 'micro-label w-48',
                    cellClassName: 'num text-dim',
                    labelMobile: 'Updated',
                    format: ({ value }) => {
                      const date = new Date(value as number);
                      return (
                        <div className="num text-dim">
                          {date.getFullYear() > 1970 ? date.toLocaleString() : 'never'}
                        </div>
                      );
                    },
                  },
                  {
                    label: 'Actions',
                    headerClassName: 'micro-label w-24 text-center',
                    cellClassName: 'text-center',
                    format: ({ item, idx }) => (
                      <Button
                        variant="outline"
                        size="sm"
                        data-tour={idx === 0 ? 'ga-refresh' : undefined}
                        onClick={() => refresh(item.email)}
                        className="border-line text-dim hover:text-foreground hover:bg-hover h-8 rounded-lg bg-transparent"
                        aria-label={`Refresh ${item.name}`}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </div>
        </section>
      )}
    </div>
  );
};
